import { useEffect, useRef, useState } from 'react';

interface ScrollNavigationItem {
  id: string;
  name: string;
}

interface UseScrollNavigationProps<T extends ScrollNavigationItem> {
  itemList: T[];
  initialSelectedId?: string;
}

interface UseScrollNavigationReturn<T extends ScrollNavigationItem> {
  selectedItemId: string;
  ref: React.RefObject<HTMLDivElement | null>;
  handleItemClick: (item: T) => void;
}

export const useScrollNavigation = <T extends ScrollNavigationItem>({
  itemList,
  initialSelectedId,
}: UseScrollNavigationProps<T>): UseScrollNavigationReturn<T> => {
  const [selectedItemId, setSelectedItemId] = useState(
    initialSelectedId || itemList[0]?.id || '',
  );
  const ref = useRef<HTMLDivElement>(null);
  const [intersectingSectionList, setIntersectingSectionList] = useState<
    string[]
  >([]);
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const isIntersecting = entry.isIntersecting;
        const sectionId = entry.target.getAttribute('data-section-id');

        if (!sectionId) return;

        if (isIntersecting) {
          setIntersectingSectionList(prev => [...prev, sectionId]);
        } else {
          setIntersectingSectionList(prev =>
            prev.filter(id => id !== sectionId),
          );
        }
      });
    },
    {
      root: null,
      threshold: [0.01, 1],
    },
  );

  useEffect(() => {
    const sectionList = itemList
      .map(item => document.querySelector(`[data-section-id="${item.id}"]`))
      .filter(element => !!element);

    sectionList.forEach(element => {
      observer.observe(element);
    });

    return () => {
      sectionList.forEach(element => {
        observer.unobserve(element);
      });
    };
  }, [itemList]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = ref.current?.scrollTop || 0;
      setScrollTop(scrollTop);
    };

    const element = ref.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        element.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  useEffect(() => {
    if (isScrolling) return;

    const sortedIntersectingSectionList = intersectingSectionList.sort();
    const container = ref.current;

    if (!container || sortedIntersectingSectionList.length === 0) return;

    const isBottom =
      container.scrollTop + container.clientHeight >= container.scrollHeight;

    if (isBottom) {
      setSelectedItemId(
        sortedIntersectingSectionList[sortedIntersectingSectionList.length - 1],
      );
      return;
    }

    setSelectedItemId(sortedIntersectingSectionList[0]);
  }, [intersectingSectionList, scrollTop, isScrolling]);

  const handleItemClick = (item: T) => {
    const section = document.querySelector(`[data-section-id="${item.id}"]`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsScrolling(true);
    setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  return {
    selectedItemId,
    ref,
    handleItemClick,
  };
};
